/**
 * DEĞİŞİKLİKTE OTOMATİK ŞİFRELİ YEDEK İNDİRME — React'siz çekirdek
 * (gelen karar 03.11.2026; `claude/notlar/2026-11-03-otomatik-indirme-kararlar.md`).
 *
 * Bulut depolama bağlı değilken kasa değişince şifreli `.pfai` dosyası
 * tarayıcının indirme yoluyla cihaza iner. Dosya bulut kopyasıyla AYNI
 * biçimde (v2, kasanın anahtarı): kasanın parolasıyla ya da 24 kelimeyle
 * açılır, ayrı parola sorulmaz.
 *
 * İKİ KADEME: kayıtlar (hesaplar, hareketler, elle fiyat, elle değerleme)
 * ya da sanal hesaplar değişince dosya iner ("veri"); yalnızca kasaya
 * kaydedilen ayarlar değişince inmez, üstte bir bant sorar ("ayar"). İkisini
 * ayırmak için son indirmedeki kasanın İKİ özeti tutulur — kasanın kendi
 * şifreli ayarlarında, profil dışında yedek durumu tutulmaz (CLAUDE.md §8).
 *
 * TARAYICI İNDİRMENİN İNİP İNMEDİĞİNİ SÖYLEMEZ: engellenen indirme sayfaya
 * haber vermez. Bu yüzden başlatılan indirme yapılmış sayılır; bu tarayıcıda
 * ilk otomatik indirmeden sonra kullanıcıya bir kez "indi mi?" sorulur
 * (cevap cihazda, `IZIN_ANAHTARI`). Ayarlar'da sorumluluk açıkça yazar.
 */

import { SON_TEYIT_AYARI } from "../kabiliyet/kabiliyet";
import { DONEMSEL_TERCIH_ANAHTARI } from "./donemselTercih";
import { ELLE_YEDEK_AYARI, OTO_INDIR_AYARI, OTO_INDIR_BEKLEME_AYARI, SON_INDIRME_AYARI } from "./yedekAyarlari";

export { OTO_INDIR_AYARI, OTO_INDIR_BEKLEME_AYARI, SON_INDIRME_AYARI };

/** Bekleme süresi seçenekleri (saniye) ve varsayılan (kullanıcı onayı 03.11.2026). */
export const BEKLEME_SECENEKLERI_SN: readonly number[] = [10, 30, 60, 300];
export const VARSAYILAN_BEKLEME_SN = 30;

/** Bu tarayıcı indirmeye izin veriyor mu — kullanıcının cevabı, yalnızca cihazda. */
export const IZIN_ANAHTARI = "parafon.otoIndirIzin";
export type IndirmeIzni = "evet" | "hayir" | null;

/** Sanal hesapların kuralları bu ayarda; kayıt gibi sayılır (rapordaki sayıları değiştirir). */
const SANAL_AYARI = "sanalHesaplar";

/** Kendiliğinden değişen ya da bu işin kendi tuttuğu ayarlar: özete girmez. */
const OZETE_GIRMEYEN: ReadonlySet<string> = new Set([
  ELLE_YEDEK_AYARI,
  SON_INDIRME_AYARI,
  OTO_INDIR_AYARI,
  OTO_INDIR_BEKLEME_AYARI,
  SON_TEYIT_AYARI,
  DONEMSEL_TERCIH_ANAHTARI,
]);

const KAYIT_ALANLARI = ["accounts", "transactions", "manualValuations", "manualPrices"] as const;

export interface IndirmeOzetleri {
  /** Kayıtlar + sanal hesaplar. */
  readonly veri: string;
  /** Geri kalan ayarlar. */
  readonly ayar: string;
  /** Canlı kayıt da sanal hesap da yok: indirilecek bir şey yok. */
  readonly bos: boolean;
}

export interface SonIndirme {
  readonly veri: string;
  readonly ayar: string;
  readonly zaman: string;
  readonly dosya: string;
  readonly tur: "oto" | "elle";
}

/** Son indirmeden beri ne değişti. */
export type IndirmeFarki = "yok" | "veri" | "ayar";

interface AyarSatiri {
  readonly key?: unknown;
  readonly deleted?: unknown;
  readonly value?: unknown;
}

async function sha(deger: unknown): Promise<string> {
  const bayt = new TextEncoder().encode(JSON.stringify(deger));
  const hash = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", bayt));
  let hex = "";
  for (const b of hash) hex += b.toString(16).padStart(2, "0");
  return hex;
}

function canli(liste: unknown): readonly unknown[] {
  if (!Array.isArray(liste)) return [];
  return liste.filter((x) => !(typeof x === "object" && x !== null && (x as { deleted?: unknown }).deleted === true));
}

function sanalVarMi(satir: AyarSatiri | undefined): boolean {
  if (satir === undefined || satir.deleted === true || typeof satir.value !== "string") return false;
  try {
    const v = JSON.parse(satir.value) as { hesaplar?: unknown } | null;
    return Array.isArray(v?.hesaplar) && v.hesaplar.length > 0;
  } catch {
    // Okunamayan kayıt yine de bir şeydir: boş sayılmaz.
    return satir.value.length > 0;
  }
}

/** `repo.exportPlainJson()` çıktısından iki özet. */
export async function indirmeOzetleri(json: string): Promise<IndirmeOzetleri> {
  const nesne = JSON.parse(json) as Record<string, unknown>;
  const ayarlar = (Array.isArray(nesne["settings"]) ? nesne["settings"] : []) as AyarSatiri[];
  const sanal = ayarlar.find((a) => a.key === SANAL_AYARI);
  const digerAyarlar = ayarlar.filter((a) => a.key !== SANAL_AYARI && !OZETE_GIRMEYEN.has(String(a.key)));
  const kayitlar = KAYIT_ALANLARI.map((k) => nesne[k] ?? null);
  const bos = KAYIT_ALANLARI.every((k) => canli(nesne[k]).length === 0) && !sanalVarMi(sanal);
  // Ayarda yalnızca anlamı: eşitleme damgası (lamport, zaman) değeri
  // değiştirmeden yenilenebilir.
  const anlam = (a: AyarSatiri | undefined) => (a === undefined ? null : [a.key, a.value, a.deleted === true]);
  return {
    veri: await sha([kayitlar, anlam(sanal)]),
    ayar: await sha(digerAyarlar.map(anlam)),
    bos,
  };
}

export function indirmeFarki(o: IndirmeOzetleri, son: SonIndirme | null): IndirmeFarki {
  if (o.bos) return "yok";
  if (son === null || son.veri !== o.veri) return "veri";
  return son.ayar !== o.ayar ? "ayar" : "yok";
}

export function sonIndirmeyiOku(metin: string | null): SonIndirme | null {
  if (metin === null) return null;
  try {
    const o = JSON.parse(metin) as Partial<SonIndirme>;
    if (typeof o.veri !== "string" || typeof o.ayar !== "string" || typeof o.zaman !== "string" || typeof o.dosya !== "string") {
      return null;
    }
    return { veri: o.veri, ayar: o.ayar, zaman: o.zaman, dosya: o.dosya, tur: o.tur === "elle" ? "elle" : "oto" };
  } catch {
    return null;
  }
}

/** Anahtar kapalı yazılmadıkça açık (varsayılan açık; bulut bağlıyken ayrıca kapanır). */
export function otoIndirAcikMi(deger: string | null): boolean {
  return deger !== "kapali";
}

export function beklemeSn(deger: string | null): number {
  const n = deger === null ? NaN : Number(deger);
  return BEKLEME_SECENEKLERI_SN.includes(n) ? n : VARSAYILAN_BEKLEME_SN;
}

/** `parafon-YYAAGGSSDDss.pfai` — İstanbul saatiyle, saniyesine kadar. */
export function otoDosyaAdi(an: Date): string {
  const parcalar = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Istanbul",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(an);
  const p = (t: Intl.DateTimeFormatPartTypes): string => parcalar.find((x) => x.type === t)?.value ?? "00";
  return `parafon-${p("year")}${p("month")}${p("day")}${p("hour")}${p("minute")}${p("second")}.pfai`;
}

export function izinOku(): IndirmeIzni {
  try {
    const v = globalThis.localStorage?.getItem(IZIN_ANAHTARI) ?? null;
    return v === "evet" || v === "hayir" ? v : null;
  } catch {
    return null;
  }
}

export function izinYaz(v: IndirmeIzni): void {
  try {
    if (v === null) globalThis.localStorage?.removeItem(IZIN_ANAHTARI);
    else globalThis.localStorage?.setItem(IZIN_ANAHTARI, v);
  } catch {
    // Depolama kapalıysa soru her açılışta yeniden sorulur; zararsız.
  }
}
